<template>
  <div class="boq_details">
    <div class="epc-data-table">
      <section class="epc-data-table__body">
        <b-table
          :key="boqId"
          class="boq-detail__table"
          :data="details"
          :loading="isLoadingDetails"
          paginated
          backend-pagination
          :total="detailTotal"
          :per-page="perPage"
          :current-page="detailPage"
          @page-change="onDetailPageChange"
          backend-sorting
          :default-sort="[detailSort.field, detailSort.dir]"
          @sort="onDetailSort"
          :narrowed="true"
          :bordered="false"
          :striped="true"
          :detailed="true"
          detail-key="id"
          :show-detail-icon="false"
          :opened-detailed="openedDetailIds"
          icon-pack="fas">

          <b-table-column label="" width="34" centered v-slot="props">
            <!-- Solo se muestra si la línea tiene mediciones vinculadas.
                 (El expandible para pictograma se analizará aparte, se comporta distinto.) -->
            <div v-if="props.row.assignments_count > 0" class="boq-detail__expand">
              <button
                type="button"
                class="boq-detail__chev"
                :title="$t('linked_measurements')"
                @click="toggleDetail(props.row)"
              >
                <b-icon
                  pack="fas"
                  :icon="openedDetailIds.includes(props.row.id) ? 'chevron-down' : 'chevron-right'"
                  size="is-small"
                />
                <span class="boq-detail__badge">{{ props.row.assignments_count }}</span>
              </button>
            </div>
          </b-table-column>

          <b-table-column field="code" :label="$t('code')" sortable v-slot="props">
            <span class="mono">{{ props.row.code }}</span>
          </b-table-column>

          <b-table-column field="line_pedido" :label="$t('line_pedido')" sortable v-slot="props">
            {{ props.row.line_pedido || '—' }}
          </b-table-column>

          <b-table-column field="description" :label="$t('description')" v-slot="props">
            <span class="clamp-2" :title="props.row.description">{{ props.row.description || '—' }}</span>
          </b-table-column>

          <b-table-column field="unit" :label="$t('unit')" v-slot="props">
            {{ props.row.unit || '—' }}
          </b-table-column>

          <b-table-column field="quantity" :label="$t('quantity')" numeric sortable v-slot="props">
            {{ formatQty(props.row.quantity) }}
          </b-table-column>

          <b-table-column field="price_unit" :label="$t('price_unit')" numeric v-slot="props">
            {{ formatMoney(props.row.price_unit) }}
          </b-table-column>

          <b-table-column field="price_total" :label="$t('price_total')" numeric v-slot="props">
            <strong>{{ formatMoney(props.row.price_total) }}</strong>
          </b-table-column>

          <b-table-column :label="$t('pending_quantity')" numeric centered v-slot="props">
            <!-- Línea marcada "sin mediciones": válida, no requiere asignación. -->
            <b-tooltip
              v-if="props.row.no_measurement_link"
              :label="$t('boq_no_measurement_message')"
              position="is-left"
              type="is-dark"
              multilined
            >
              <span class="boq-nolink-flag">
                <b-icon pack="fas" icon="link-slash" size="is-small" />
              </span>
            </b-tooltip>
            <span v-else-if="props.row.assignments_count > 0" class="inline-flex items-center justify-center gap-1">
              <span
                class="boq-status"
                :class="boqStatusClass(props.row)"
                :title="boqStatusLabel(props.row)"
              >
                {{ boqPendingDisplay(props.row) }}
              </span>

              <!-- Cubierto exacto: check verde, sin tooltip. -->
              <b-icon
                v-if="boqIsOk(props.row)"
                pack="fas"
                icon="circle-check"
                size="is-small"
                class="boq-pending-ok"
              />

              <!-- Excedente: la cantidad sobrante debe certificarse en otra BOQ.
                  El icono avisa y al pulsarlo abre/carga el detalle de la línea. -->
              <b-tooltip
                v-else-if="boqIsExcess(props.row)"
                :label="$t('boq_excess_other_boq_hint')"
                position="is-left"
                type="is-danger"
                multilined
              >
                <button type="button" class="boq-excess-flag" @click="toggleDetail(props.row)">
                  <b-icon pack="fas" icon="triangle-exclamation" size="is-small" />
                </button>
              </b-tooltip>

              <!-- Déficit: falta cantidad por cubrir; sugiere vincular más mediciones. -->
              <b-tooltip
                v-else
                :label="$t('boq_deficit_link_more_hint')"
                position="is-left"
                type="is-warning"
                multilined
              >
                <span class="boq-deficit-flag">
                  <b-icon pack="fas" icon="circle-exclamation" size="is-small" />
                </span>
              </b-tooltip>
            </span>
            <span v-else style="color:#cbd5e1">—</span>
          </b-table-column>

          <!-- Vínculo: medición (SQL, todas las disciplinas) y/o elemento de pictograma (Mongo,
               solo mec/elec). Chips de icono compactos con tooltip. -->
          <b-table-column :label="$t('boq_linkage')" centered v-slot="props">
            <span class="inline-flex items-center justify-center gap-1">
              <b-tooltip v-if="props.row.assignments_count > 0" :label="$t('measurement')" position="is-left" type="is-dark">
                <span class="boq-link-chip boq-link-chip--meas">
                  <b-icon pack="fas" icon="ruler-combined" size="is-small" />
                </span>
              </b-tooltip>
              <b-tooltip v-if="props.row.pictogram_elements_count > 0" :label="$t('pictogram')" position="is-left" type="is-dark">
                <span class="boq-link-chip boq-link-chip--pic">
                  <b-icon pack="fas" icon="shapes" size="is-small" />
                </span>
              </b-tooltip>
              <b-tooltip
                v-if="!props.row.assignments_count && !props.row.pictogram_elements_count && !props.row.no_measurement_link"
                :label="$t('boq_link_none')"
                position="is-left"
                type="is-dark"
              >
                <span class="boq-link-chip boq-link-chip--none">
                  <b-icon pack="fas" icon="link-slash" size="is-small" />
                </span>
              </b-tooltip>
              <!-- Marcada "sin medición" (no requiere vínculo): guion. -->
              <span
                v-if="!props.row.assignments_count && !props.row.pictogram_elements_count && props.row.no_measurement_link"
                style="color:#cbd5e1"
              >—</span>
            </span>
          </b-table-column>

          <template #detail="props">
            <!-- Mediciones vinculadas (SQL). Se oculta si la línea es solo de pictograma. -->
            <div v-if="props.row.assignments_count > 0 || !props.row.pictogram_elements_count" class="boq-linked">
              <div class="boq-linked__title">{{ $t('linked_measurements') }}</div>
              <div v-if="isLinkedLoading(props.row.id)" class="boq-linked__empty">
                <b-icon pack="fas" icon="spinner" size="is-small" class="fa-spin" /> {{ $t('loading') }}
              </div>
              <div v-else-if="!linkedMeasurements(props.row.id).length" class="boq-linked__empty">
                {{ $t('no_results') }}
              </div>
              <table v-else class="boq-linked__table">
                <thead>
                  <tr>
                    <th>{{ $t('code') }}</th>
                    <th>{{ $t('name') }}</th>
                    <th class="is-right">{{ $t('quantity') }}</th>
                    <th>{{ $t('unit') }}</th>
                    <th class="is-center">{{ $t('status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in markedLinked(props.row)" :key="'lm_' + m.id" :class="{ 'boq-linked__row--excess': m._excess }">
                    <td class="mono">{{ m.code || '—' }}</td>
                    <td>
                      <span class="inline-flex items-center gap-1">
                        {{ m.name || '—' }}
                        <button
                          v-if="m._excess"
                          type="button"
                          class="boq-split-btn"
                          :title="$t('split_measurement_title')"
                          @click="openSplit(props.row, m)"
                        >
                          <b-icon pack="fas" icon="scissors" size="is-small" />
                        </button>
                      </span>
                    </td>
                    <td class="is-right">{{ formatQty(m.quantity) }}</td>
                    <td>{{ m.unit || '—' }}</td>
                    <td class="is-center">
                      <!-- Excedente: icono rojo con el aviso de que la cantidad debe certificarse en otra BOQ. -->
                      <b-tooltip
                        v-if="m._excess"
                        :label="$t('boq_excess_other_boq_hint')"
                        position="is-left"
                        type="is-danger"
                        multilined
                      >
                        <span class="boq-excess-flag">
                          <b-icon pack="fas" icon="triangle-exclamation" size="is-small" />
                        </span>
                      </b-tooltip>
                      <span v-else class="boq-status boq-status--ok">{{ $t('boq_status_ok') }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Elementos de pictograma vinculados (Mongo, solo mec/elec). -->
            <div v-if="props.row.pictogram_elements_count > 0" class="boq-linked" style="margin-top:10px">
              <div class="boq-linked__title">{{ $t('pictogram_elements') }}</div>
              <div v-if="isPictoLoading(props.row.id)" class="boq-linked__empty">
                <b-icon pack="fas" icon="spinner" size="is-small" class="fa-spin" /> {{ $t('loading') }}
              </div>
              <div v-else-if="!linkedPictoElements(props.row.id).length" class="boq-linked__empty">
                {{ $t('no_results') }}
              </div>
              <table v-else class="boq-linked__table">
                <thead>
                  <tr>
                    <th>{{ $t('code') }}</th>
                    <th>{{ $t('name') }}</th>
                    <th>{{ $t('type') }}</th>
                    <th>{{ $t('layer') }}</th>
                    <th class="is-right">{{ $t('length') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(el, i) in linkedPictoElements(props.row.id)" :key="'pe_' + (el.handle || i)">
                    <td class="mono">{{ el.code || el.handle || '—' }}</td>
                    <td>{{ el.name || '—' }}</td>
                    <td>{{ el.type || '—' }}</td>
                    <td>{{ el.layer_name || '—' }}</td>
                    <td class="is-right">{{ el.total_length != null ? formatQty(el.total_length) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template #empty>
            <div class="has-text-centered py-4" style="color:#9aa7c0;">
              {{ isLoadingDetails ? '' : $t('no_results') }}
            </div>
          </template>
        </b-table>
      </section>
    </div>

    <!-- Footer siempre visible: nº total de líneas + total (del conjunto completo). -->
    <div v-if="detailTotal" class="boq-detail__foot">
      <span>{{ detailTotal }}</span>
      <span class="boq-detail__foot-total">{{ formatMoney(detailAggTotal) }}</span>
    </div>

    <!-- Modal: dividir (split) medición excedente. El contenido/lógica vive en SplitAssignment. -->
    <epc-modal-wrapper :active.sync="showSplitModal" :title="$t('split_measurement_title')" :width="640" :can-cancel="['escape']" scroll-body>
      <split-assignment
        v-if="showSplitModal && splitTarget"
        ref="splitCmp"
        :target="splitTarget"
        :covered="splitContext.covered"
        :excess="splitContext.excess"
        @busy="isSplitting = $event"
        @saved="onSplitSaved"
      />
      <template #footer>
        <button class="epc-modal__button" @click="showSplitModal = false">{{ $t('cancel') }}</button>
        <button
          class="epc-modal__button epc-modal__button--primary"
          :class="{ 'epc-modal__button--disabled': isSplitting }"
          :disabled="isSplitting"
          @click="$refs.splitCmp && $refs.splitCmp.submit()"
        >{{ isSplitting ? $t('saving') : $t('split_measurement_confirm') }}</button>
      </template>
    </epc-modal-wrapper>
  </div>
</template>

<script>
  import axios from "axios";
  import EpcModalWrapper from "../../epc-modal-wrapper.vue";
  import SplitAssignment from "./SplitAssignment.vue";

  export default {
    name: 'BOQDetails',

    components: { EpcModalWrapper, SplitAssignment },

    props: {
      projectId: { type: Number, required: true },
      // Carga BOQ seleccionada (cabecera). Al cambiar de id se recarga el detalle.
      boq: { type: Object, default: null },
    },

    data() {
      return {
        perPage: 25,
        details: [],
        isLoadingDetails: false,
        // Paginación / orden en servidor.
        detailPage: 1,
        detailTotal: 0,
        detailAggTotal: 0,
        detailSort: { field: 'code', dir: 'asc' },
        // Filas expandidas + caché de mediciones vinculadas.
        openedDetailIds: [],
        linkedByDetail: {},
        // Caché de elementos de pictograma vinculados (mec/elec).
        pictoByDetail: {},
        // Split de medición excedente (el contenido/lógica vive en SplitAssignment).
        showSplitModal: false,
        isSplitting: false,          // reflejo del estado del hijo (@busy) para el footer.
        splitTarget: null,
        splitContext: { covered: 0, excess: 0 },
      };
    },

    computed: {
      boqId() {
        return this.boq ? this.boq.id : null;
      },
    },

    watch: {
      // Al cambiar de carga: primera página y recarga.
      boqId(newId, oldId) {
        if (newId && newId !== oldId) {
          this.detailPage = 1;
          this.loadDetails();
        }
      },
    },

    created() {
      if (this.boqId) this.loadDetails();
    },

    methods: {
      // Recarga pública (para el padre tras cerrar el modal de vinculación / importar).
      reload() {
        return this.loadDetails({ preserveOpened: true });
      },

      // Carga (paginada en servidor) las líneas de la carga. Emite stats al padre.
      async loadDetails(opts = {}) {
        if (!this.boqId) return;
        const preserveOpened = !!opts.preserveOpened;
        const opened = preserveOpened ? [...this.openedDetailIds] : [];

        this.isLoadingDetails = true;
        this.$emit('loading', true);
        this.details = [];
        this.openedDetailIds = [];
        this.linkedByDetail = {};
        try {
          const { data } = await axios.get(
            `/api/v2/project/${this.projectId}/boq-details`,
            {
              params: {
                boq_id: this.boqId,
                per_page: this.perPage,
                page: this.detailPage,
                sort_field: this.detailSort.field || undefined,
                sort_dir: this.detailSort.dir || undefined,
              },
            }
          );
          if (data && data.success) {
            this.details = data.boqs || [];
            this.detailTotal = (data.meta && data.meta.total != null) ? data.meta.total : this.details.length;
            this.detailAggTotal = (data.aggregates && data.aggregates.sum_price_total) || 0;
            // Stats para la cabecera del padre (chip de validación).
            this.$emit('stats', {
              total: this.detailTotal,
              aggTotal: this.detailAggTotal,
              observed: (data.aggregates && data.aggregates.observed_count) || 0,
              unassigned: (data.aggregates && data.aggregates.unassigned_count) || 0,
            });
          }
          // Reabre las filas que estaban expandidas (split / recarga externa).
          if (preserveOpened && opened.length) {
            this.openedDetailIds = opened.filter((id) => this.details.some((d) => d.id === id));
            this.openedDetailIds.forEach((id) => {
              this.loadLinkedMeasurements(id);
              const r = this.details.find((d) => d.id === id);
              if (r && r.pictogram_elements_count > 0) {
                this.loadLinkedPictogramElements(id);
              }
            });
          }
        } catch (e) {
          this.$notify.error(this.$t('error_loading_documents'));
        } finally {
          this.isLoadingDetails = false;
          this.$emit('loading', false);
        }
      },

      onDetailPageChange(page) {
        this.detailPage = page;
        this.loadDetails();
      },
      onDetailSort(field, order) {
        this.detailSort = { field, dir: order || 'asc' };
        this.detailPage = 1;
        this.loadDetails();
      },

      // --- Estado de cobertura de la línea BOQ (pendiente = total − Σ vinculadas) ---
      boqLinked(row) {
        return Number(row.linked_quantity || 0);
      },
      boqPending(row) {
        return (Number(row.quantity) || 0) - this.boqLinked(row);
      },
      boqPendingText(row) {
        const p = this.boqPending(row);
        return Number(p).toLocaleString('es-ES', { maximumFractionDigits: 4 });
      },
      boqIsExcess(row) {
        return this.boqPending(row) < -1e-9;
      },
      boqIsOk(row) {
        return Math.abs(this.boqPending(row)) < 1e-9;
      },
      boqIsDeficit(row) {
        return this.boqPending(row) > 1e-9;
      },
      boqPendingDisplay(row) {
        if (this.boqIsExcess(row)) return '0';
        return this.boqPendingText(row);
      },
      boqStatusClass(row) {
        const p = this.boqPending(row);
        if (p > 1e-9) return 'boq-status--deficit';
        return 'boq-status--ok';
      },
      boqStatusLabel(row) {
        const p = this.boqPending(row);
        if (p > 1e-9) return this.$t('boq_status_deficit');
        return this.$t('boq_status_ok');
      },
      // Marca (regla de acumulado por id) qué mediciones exceden el total de la línea.
      markedLinked(row) {
        const items = this.linkedMeasurements(row.id);
        const total = Number(row.quantity) || 0;
        const ordered = items.slice().sort((a, b) => a.id - b.id);
        let running = 0;
        return ordered.map((m) => {
          const q = Number(m.quantity) || 0;
          let excess = false;
          let coveredQty = q;
          let excessQty = 0;
          if (total > 0 && q > 0) {
            if (running >= total - 1e-9) { excess = true; coveredQty = 0; excessQty = q; }
            else if (running + q > total + 1e-9) { excess = true; coveredQty = total - running; excessQty = q - coveredQty; }
          }
          running += (q > 0 ? q : 0);
          return { ...m, _excess: excess, _coveredQty: coveredQty, _excessQty: excessQty };
        });
      },

      // --- Split de medición excedente (el modal usa <SplitAssignment>) ---
      openSplit(row, m) {
        this.splitTarget = m;
        this.splitContext = {
          covered: Number(m._coveredQty) || 0,
          excess: Number(m._excessQty) || 0,
        };
        this.showSplitModal = true;
      },
      // Tras un split OK (emitido por SplitAssignment): cierra el modal y recarga el detalle.
      async onSplitSaved() {
        this.showSplitModal = false;
        await this.loadDetails({ preserveOpened: true });
      },

      // --- Detalle expandible: mediciones vinculadas a la línea BOQ ---
      linkedMeasurements(id) {
        const e = this.linkedByDetail[id];
        return e && e.items ? e.items : [];
      },
      isLinkedLoading(id) {
        const e = this.linkedByDetail[id];
        return !!(e && e.loading);
      },
      linkedPictoElements(id) {
        const e = this.pictoByDetail[id];
        return e && e.items ? e.items : [];
      },
      isPictoLoading(id) {
        const e = this.pictoByDetail[id];
        return !!(e && e.loading);
      },
      async loadLinkedPictogramElements(id) {
        this.$set(this.pictoByDetail, id, { loading: true, items: [] });
        try {
          const { data } = await axios.get(
            `/api/v2/project/${this.projectId}/boq-details/${id}/pictogram-elements`
          );
          const items = (data && data.success && data.elements) ? data.elements : [];
          this.$set(this.pictoByDetail, id, { loading: false, items });
        } catch (e) {
          this.$set(this.pictoByDetail, id, { loading: false, items: [] });
        }
      },
      toggleDetail(row) {
        const id = row.id;
        const i = this.openedDetailIds.indexOf(id);
        if (i >= 0) {
          this.openedDetailIds = this.openedDetailIds.filter((x) => x !== id);
        } else {
          this.openedDetailIds = [...this.openedDetailIds, id];
          this.loadLinkedMeasurements(id);
          if (row.pictogram_elements_count > 0) {
            this.loadLinkedPictogramElements(id);
          }
        }
      },
      async loadLinkedMeasurements(id) {
        this.$set(this.linkedByDetail, id, { loading: true, items: [] });
        try {
          const { data } = await axios.get(
            `/api/v2/project/${this.projectId}/boq-details/${id}/assignments`
          );
          const items = (data && data.success && data.assignments) ? data.assignments : [];
          this.$set(this.linkedByDetail, id, { loading: false, items });
          const row = this.details.find((d) => d.id === id);
          if (row) this.$set(row, 'assignments_count', items.length);
        } catch (e) {
          this.$set(this.linkedByDetail, id, { loading: false, items: [] });
        }
      },

      // --- formato ---
      formatMoney(v) {
        const n = parseFloat(v);
        if (!Number.isFinite(n)) return '—';
        return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      formatQty(v) {
        const n = parseFloat(v);
        if (!Number.isFinite(n)) return '—';
        return n.toLocaleString('es-ES', { maximumFractionDigits: 4 });
      },
    },
  };
</script>

<style scoped>
/* Scroll interno: las filas scrollean dentro del contenedor con la cabecera fija. */
.boq_details >>> .table-wrapper {
  max-height: 56vh;
  overflow: auto;
}
.boq_details >>> .table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

/* Footer del detalle: nº de líneas + total (siempre visible) */
.boq-detail__foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin-top: 4px;
  border-top: 1px solid #eef1f7;
  font-size: 13px;
  color: #7d8aa5;
}
.boq-detail__foot-total { font-weight: 700; color: #33518f; }

/* Descripción: máximo 2 líneas con ellipsis (tooltip para el texto completo) */
.clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 340px;
  line-height: 1.3;
  white-space: normal;
}
.mono { font-family: monospace; font-size: 12px; color: #385da5; }

/* Tabla de detalles más compacta */
.boq-detail__table >>> td,
.boq-detail__table >>> th {
  padding-top: 0.2rem !important;
  padding-bottom: 0.2rem !important;
}

/* Columna chevron + badge de conteo */
.boq-detail__expand { display: inline-flex; align-items: center; gap: 4px; }
.boq-detail__chev {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 1px 3px;
  line-height: 1;
  border-radius: 4px;
}
.boq-detail__chev:hover { background: #eef1f6; color: #3d5c9e; }
.boq-detail__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 9999px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: #3d5c9e;
}

/* Tabla de mediciones vinculadas (fila expandida) */
.boq-linked { padding: 0.35rem 0.5rem; }
.boq-linked__title { font-size: 11px; font-weight: 700; color: #3d5c9e; margin-bottom: 0.35rem; }
.boq-linked__empty { font-size: 11px; color: #9aa7c0; padding: 0.25rem 0; }
.boq-linked__table { width: 100%; border-collapse: collapse; font-size: 11px; }
.boq-linked__table th,
.boq-linked__table td { padding: 3px 8px; border-top: 1px solid #eef1f6; text-align: left; }
.boq-linked__table thead th { background: #f5f7fb; color: #55627d; font-weight: 600; border-top: 0; }
.boq-linked__table .is-right { text-align: right; }
.boq-linked__table .is-center { text-align: center; }
.boq-linked__row--excess td { background: #fff7ed; }

/* Indicador "sin mediciones" en la columna pendiente (línea que no se vincula). */
.boq-nolink-flag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(87,83,78);
}

/* Chips compactos de vínculo (medición / pictograma / sin vincular) en mec/elec. */
.boq-link-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
}
.boq-link-chip--meas { color: #2563eb; background: #eff6ff; }
.boq-link-chip--pic  { color: #16a34a; background: #f0fdf4; }
.boq-link-chip--none { color: #94a3b8; background: #f1f5f9; }

/* Botón split (tijera) en la fila excedente */
.boq-split-btn {
  border: 0;
  background: transparent;
  color: #b42318;
  cursor: pointer;
  padding: 0 2px;
  border-radius: 4px;
  line-height: 1;
  display: inline-flex;
}
.boq-split-btn:hover { color: #7a1b13; }

</style>
